import * as React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

export interface OnboardingTooltipProps {
  /** Whether to show the tooltip */
  show: boolean;
  /** Message to display in the tooltip */
  message: string;
  /** Ref to the target element the tooltip should point at */
  targetRef: React.RefObject<HTMLElement | null>;
  /** Position of the tooltip relative to the target */
  position?: 'top' | 'bottom';
  /** Callback when tooltip should be dismissed */
  onDismiss?: () => void;
  /** Auto-dismiss timeout in milliseconds (default: 60000) */
  autoDismissTimeout?: number;
}

/**
 * OnboardingTooltip - Renders a tooltip pointing at a target element
 *
 * Uses the Hollywood Trucks brand green (#8FB560) for styling.
 * Rendered via portal at document body level to avoid stacking context issues.
 * Hides on ANY click anywhere on the page.
 */
export function OnboardingTooltip({
  show,
  message,
  targetRef,
  position = 'bottom',
  onDismiss,
  autoDismissTimeout = 60000,
}: OnboardingTooltipProps) {
  const [tooltipPosition, setTooltipPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [temporarilyHidden, setTemporarilyHidden] = React.useState(false);

  // Hide tooltip on ANY click anywhere on the page
  React.useEffect(() => {
    if (!show || temporarilyHidden) return;

    const handleGlobalClick = () => {
      setTemporarilyHidden(true);
    };

    // Use capture phase to catch clicks before they're handled by other elements
    document.addEventListener('click', handleGlobalClick, true);
    return () => document.removeEventListener('click', handleGlobalClick, true);
  }, [show, temporarilyHidden]);

  // Calculate tooltip position based on target element
  React.useEffect(() => {
    if (!show || temporarilyHidden || !targetRef.current) {
      setTooltipPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!targetRef.current) return;

      const rect = targetRef.current.getBoundingClientRect();
      let top = 0;
      const left = rect.left + rect.width / 2; // Center horizontally on target

      if (position === 'top') {
        top = rect.top - 12; // 12px gap above
      } else {
        top = rect.bottom + 12; // 12px gap below
      }

      setTooltipPosition({ top, left });
    };

    updatePosition();

    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [show, temporarilyHidden, targetRef, position]);

  // Auto-dismiss after timeout
  React.useEffect(() => {
    if (!show || temporarilyHidden || !onDismiss || autoDismissTimeout <= 0) return;

    const timer = setTimeout(() => {
      onDismiss();
    }, autoDismissTimeout);

    return () => clearTimeout(timer);
  }, [show, temporarilyHidden, onDismiss, autoDismissTimeout]);

  // Reset temporarily hidden state when show becomes false
  React.useEffect(() => {
    if (!show) {
      setTemporarilyHidden(false);
    }
  }, [show]);

  const isVisible = show && !temporarilyHidden;

  // Get x/y offsets for Framer Motion based on position
  // Using Framer Motion's x property for centering avoids conflicts with y animation
  const getMotionX = () => '-50%'; // Always center horizontally
  const getMotionY = () => (position === 'top' ? '-100%' : '0%');

  // Arrow styles based on position
  const getArrowStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
    };

    if (position === 'top') {
      return {
        ...base,
        bottom: -6,
        borderTop: '6px solid #8FB560',
      };
    }
    return {
      ...base,
      top: -6,
      borderBottom: '6px solid #8FB560',
    };
  };

  if (!isVisible || !tooltipPosition) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{
          opacity: 0,
          x: getMotionX(),
          y: position === 'top' ? `calc(${getMotionY()} - 8px)` : `calc(${getMotionY()} + 8px)`
        }}
        animate={{
          opacity: 1,
          x: getMotionX(),
          y: getMotionY()
        }}
        exit={{
          opacity: 0,
          x: getMotionX(),
          y: position === 'top' ? `calc(${getMotionY()} - 8px)` : `calc(${getMotionY()} + 8px)`
        }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        <div className="relative bg-[#8FB560] rounded-md shadow-lg px-3 py-2 whitespace-nowrap">
          <div style={getArrowStyles()} />
          <span className="text-sm font-semibold text-black">{message}</span>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

export default OnboardingTooltip;
